import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import CustomModal from '../reusable/CustomModal';
import { getEOAs } from '../../services/userService';
import { inviteUser, updateUser } from '../../services/userManagementService';
import { UserListItem } from '../../interfaces/UserManagementInterface';
import { EOAListItem } from '../../interfaces/UserDetailsInterface';
import { EOA_INVITE_ROLES, PLATFORM_INVITE_ROLES, USER_ROLES, USER_ROLES_LABELS } from '../../constants';
import { applyRoleSelection, buildRolePayloads, getErrorDetail, requiresCohortSelection } from './userManagementUtils';
import './UserManagementLayout.css';

const ASSIGNABLE_ROLES: string[] = [...EOA_INVITE_ROLES, ...PLATFORM_INVITE_ROLES];

const formatEoaLabel = (eoa: EOAListItem) => `${eoa.district.name} (${eoa.year_ending - 1}-${eoa.year_ending})`;

interface UserFormModalProps {
  // null => Add mode, a user => Edit mode
  user: UserListItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const UserFormModal = ({ user, isOpen, onClose, onSaved }: UserFormModalProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const isAddMode = user === null;
  // Only a Super Admin may grant or revoke the Super Admin role (enforced on the backend too).
  const isCurrentUserSuperAdmin = userDetails?.role === USER_ROLES.SUPER_ADMIN;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [cohortEoaIds, setCohortEoaIds] = useState<string[]>([]);
  const [eoas, setEoas] = useState<EOAListItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const loadFormData = useCallback(async () => {
    if (!userDetails) return;
    try {
      const eoasResponse = await getEOAs(userDetails.access_token);
      setEoas(eoasResponse.data.eoas);
    } catch (error: unknown) {
      console.error('Error loading EOAs:', error);
    }
  }, [userDetails]);

  useEffect(() => {
    if (!isOpen) return;
    loadFormData();
    if (user) {
      const nameParts = (user.name ?? '').trim().split(/\s+/).filter(Boolean);
      setFirstName(nameParts[0] ?? '');
      setLastName(nameParts.slice(1).join(' '));
      setEmail(user.email);
      setSelectedRoles(user.roles ?? []);
      setCohortEoaIds(user.eoa_ids ?? []);
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
      setSelectedRoles([]);
      setCohortEoaIds([]);
    }
  }, [isOpen, user, loadFormData]);

  const handleRolesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    setSelectedRoles(applyRoleSelection(next, selectedRoles, isCurrentUserSuperAdmin));
  };

  const handleCohortsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setCohortEoaIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = async () => {
    if (!userDetails) return;

    const name = `${firstName.trim()} ${lastName.trim()}`.trim();

    if (requiresCohortSelection(selectedRoles, cohortEoaIds)) {
      handleOpenErrorModal(
        'Please select at least one EOAA Cohort for the assigned EOA role(s).',
        new Error('Missing EOAA Cohort')
      );
      return;
    }

    const { platform_roles, eoa_roles } = buildRolePayloads(selectedRoles, cohortEoaIds);

    try {
      setSubmitting(true);
      if (isAddMode) {
        if (!name || !email.trim()) {
          handleOpenErrorModal('Name and email are required.', new Error('Missing name or email'));
          setSubmitting(false);
          return;
        }
        await inviteUser(userDetails.access_token, {
          name,
          email: email.trim(),
          platform_roles,
          eoa_roles,
        });
        onSaved(`Invitation sent to ${email.trim()}.`);
      } else {
        await updateUser(userDetails.access_token, user.id, {
          name,
          platform_roles,
          eoa_roles,
        });
        onSaved(`${user.name} updated.`);
      }
    } catch (error: unknown) {
      console.error('Error saving user:', error);
      handleOpenErrorModal(
        getErrorDetail(
          error,
          isAddMode ? 'Failed to add user. Please try again.' : 'Failed to update user. Please try again.'
        ),
        error as Error
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomModal
      title={isAddMode ? 'Add New User' : (user?.name ?? 'Edit User')}
      isOpen={isOpen}
      handleClose={onClose}
      icon={false}
      maxWidth="sm"
    >
      <div className="user-management-invite-form">
        <FormControl fullWidth>
          <InputLabel id="user-form-role-label">Role</InputLabel>
          <Select
            labelId="user-form-role-label"
            multiple
            value={selectedRoles}
            onChange={handleRolesChange}
            input={<OutlinedInput label="Role" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((role) => (
                  <Chip key={role} label={USER_ROLES_LABELS[role] ?? role} size="small" />
                ))}
              </Box>
            )}
          >
            {ASSIGNABLE_ROLES.map((role) => {
              const superAdminLocked = role === USER_ROLES.SUPER_ADMIN && !isCurrentUserSuperAdmin;
              const label = USER_ROLES_LABELS[role] ?? role;
              return (
                <MenuItem key={role} value={role} disabled={superAdminLocked}>
                  <Checkbox checked={selectedRoles.indexOf(role) > -1} />
                  <ListItemText
                    primary={superAdminLocked ? `${label} (view only)` : label}
                    sx={superAdminLocked ? { '& .MuiListItemText-primary': { color: 'text.disabled' } } : undefined}
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="user-form-cohort-label">EOAA Cohort</InputLabel>
          <Select
            labelId="user-form-cohort-label"
            multiple
            value={cohortEoaIds}
            onChange={handleCohortsChange}
            input={<OutlinedInput label="EOAA Cohort" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((eoaId) => {
                  const eoa = eoas.find((item) => item.id === eoaId);
                  return <Chip key={eoaId} label={eoa ? formatEoaLabel(eoa) : eoaId} size="small" />;
                })}
              </Box>
            )}
          >
            {eoas.map((eoa) => (
              <MenuItem key={eoa.id} value={eoa.id}>
                <Checkbox checked={cohortEoaIds.indexOf(eoa.id) > -1} />
                <ListItemText primary={formatEoaLabel(eoa)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="First Name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          fullWidth
        />
        <TextField label="Last Name" value={lastName} onChange={(event) => setLastName(event.target.value)} fullWidth />
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
          disabled={!isAddMode}
          helperText={isAddMode ? undefined : 'Email cannot be changed'}
        />

        <div className="user-management-form-actions">
          <CustomButton variant="text" buttonText="Cancel" buttonAction={onClose} disabled={submitting} />
          <CustomButton
            variant="primary"
            buttonText={isAddMode ? 'Add User' : 'Save'}
            buttonAction={handleSubmit}
            disabled={submitting}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default UserFormModal;
